import { useParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { Skeleton } from '@/components/States'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney } from '@/lib/money'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const full = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

export function TransactionDetailScreen() {
  const { id } = useParams()
  const repository = useRepository()
  const transactions = useQuery(() => repository.listTransactions(), [repository])

  if (transactions.status === 'loading') return <Skeleton height={360} />
  const tx = transactions.data?.find((item) => item.id === id)
  if (!tx) return <NotFoundScreen />

  const incoming = tx.direction === 'in'

  return (
    <>
      <PageHeader
        title={tx.counterparty}
        subtitle={full.format(new Date(tx.occurredAt))}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="download">
              Receipt
            </Button>
            <Button variant="ghost" size="md" iconStart="flag" to="/support">
              Something is wrong
            </Button>
          </>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone={incoming ? 'dark' : 'light'}>
            <Card.Header>
              <Stack direction="row" align="center" gap={3}>
                <Icon name={tx.icon} size={22} />
                <Text variant="caption" weight="semibold" tone={incoming ? 'onDark' : 'primary'}>
                  {tx.category}
                </Text>
              </Stack>
              <Pill tone={tx.status === 'settled' ? 'positive' : 'warning'}>{tx.status}</Pill>
            </Card.Header>
            <Stack gap={1} className="pb-mt-4">
              <Text
                as="p"
                variant="display"
                weight="bold"
                numeric
                tone={incoming ? 'accent' : 'primary'}
              >
                {formatMoney(tx.amount, { signed: incoming })}
              </Text>
              {tx.cashback && (
                <Text variant="caption" weight="semibold" tone="positive" numeric>
                  {formatMoney(tx.cashback, { signed: true })} cashback, paid on the 1st
                </Text>
              )}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Details
            </Text>
            <Stack gap={0} className="pb-mt-2">
              {[
                ['Status', tx.status === 'settled' ? 'Settled' : 'Pending'],
                ['Paid with', incoming ? 'Incoming transfer' : 'Mastercard ••4568'],
                ['Account', 'US Dollar · ••4568'],
                ['Reference', `PB-${tx.id.toUpperCase().replace('TXN_', '44')}-90A2`],
                ['FX', incoming ? '—' : 'No conversion needed'],
              ].map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold" numeric>
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Stack direction="row" align="start" gap={3}>
              <Icon name="info" size={20} />
              <Text variant="caption" tone="secondary">
                Refunds are returned to the same account and any cashback earned on this purchase is
                taken back off. A merchant usually takes three to five working days.
              </Text>
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
